import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { fetchSettlementDetail, fetchSettlementReviewRecords, fetchConfirmSettlement, fetchDisputeSettlement } from '@/service/api/revenue';
import { getRevenueStatusInfo } from '@/utils/revenue';
import { formatDate, formatMoney } from '@/pages/projects/modules/utils';

interface SettlementDetailModalProps {
  settlementId: string | null;
  onClose: () => void;
  onRefresh?: () => void;
}

export default function SettlementDetailModal({ settlementId, onClose, onRefresh }: SettlementDetailModalProps) {
  const [detail, setDetail] = useState<Api.Revenue.SettlementDetailResponse | null>(null);
  const [reviewRecords, setReviewRecords] = useState<Api.Revenue.SettlementReviewRecordResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [confirming, setConfirming] = useState<boolean>(false);
  const [showDisputeForm, setShowDisputeForm] = useState<boolean>(false);
  const [disputeNote, setDisputeNote] = useState<string>('');
  const [submittingDispute, setSubmittingDispute] = useState<boolean>(false);

  useEffect(() => {
    if (!settlementId) return;

    let isMounted = true;
    setLoading(true);
    setError('');

    Promise.all([
      fetchSettlementDetail(settlementId),
      fetchSettlementReviewRecords(settlementId).catch(() => []),
    ])
      .then(([detailRes, recordsRes]) => {
        if (!isMounted) return;
        if (detailRes) {
          setDetail(detailRes);
        }
        if (Array.isArray(recordsRes)) {
          setReviewRecords(recordsRes);
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err instanceof Error ? err.message : '加载结算详情失败');
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [settlementId]);

  if (!settlementId) return null;

  const handleConfirm = async () => {
    if (!settlementId) return;
    if (!confirm('确认完成该笔结算单的核对与确认？确认后将提交结算申请（需结算资料审核通过）。')) return;

    setConfirming(true);
    try {
      await fetchConfirmSettlement(settlementId);
      alert('结算单确认成功！');
      onRefresh?.();
      onClose();
    } catch (err) {
      alert('确认结算单失败: ' + (err instanceof Error ? err.message : '网络错误'));
    } finally {
      setConfirming(false);
    }
  };

  const statusInfo = getRevenueStatusInfo(detail?.status);

  /** 格式化分成比例说明 */
  const formatRateText = (rate?: number | string) => {
    if (rate === undefined || rate === null || rate === '') return '-';
    const num = Number(rate);
    if (isNaN(num)) return '-';
    const val = num <= 1 ? num * 100 : num;
    return `${val.toFixed(0)}%`;
  };

  const canConfirm =
    detail?.status === 'PENDING_OWNER_CONFIRMATION' ||
    detail?.status === 'PENDING_PROFILE' ||
    detail?.status === 'PENDING';

  const canDispute =
    canConfirm ||
    detail?.status === 'ISSUED' ||
    detail?.status === 'OWNER_CONFIRMED';

  const handleDispute = async () => {
    if (!settlementId) return;
    if (!disputeNote.trim()) {
      alert('请填写异议原因/说明');
      return;
    }

    setSubmittingDispute(true);
    try {
      await fetchDisputeSettlement(settlementId, { note: disputeNote.trim() });
      alert('账单异议已成功提交，平台客服与财务将进行复核');
      setShowDisputeForm(false);
      setDisputeNote('');
      onRefresh?.();
      onClose();
    } catch (err) {
      alert('提交异议失败: ' + (err instanceof Error ? err.message : '网络错误'));
    } finally {
      setSubmittingDispute(false);
    }
  };

  const snapshot = detail?.settlement_profile_snapshot || {};
  const payment = detail?.payment;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-100" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center text-primary-600">
              <i className="ri-bank-card-line text-lg"></i>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-foreground-950 text-base">结算申请详情</h3>
                {detail?.status && (
                  <span className={`text-xs px-2.5 py-0.5 rounded-full border font-medium whitespace-nowrap shrink-0 ${statusInfo.color}`}>
                    {statusInfo.label}
                  </span>
                )}
              </div>
              <p className="text-xs text-foreground-400 mt-0.5">
                结算单号：{detail?.settlement_no || detail?.id || settlementId}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:text-foreground-700 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1 text-sm">
          {loading ? (
            <div className="py-16 text-center text-foreground-400">
              <i className="ri-loader-4-line text-3xl block mb-2 animate-spin text-primary-500"></i>
              正在加载结算申请详情...
            </div>
          ) : error ? (
            <div className="py-12 text-center text-rose-500">
              <i className="ri-error-warning-line text-3xl block mb-2 text-rose-400"></i>
              {error}
            </div>
          ) : detail ? (
            <>
              {/* 1. 基础摘要 & 核心数据 */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50/80 p-4 rounded-xl border border-gray-200/60">
                <div>
                  <span className="text-xs text-foreground-400 block mb-1">结算周期</span>
                  <span className="font-semibold text-foreground-900">
                    {detail.period_start_date
                      ? `${formatDate(detail.period_start_date)} ~ ${formatDate(detail.period_end_date)}`
                      : '-'}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-foreground-400 block mb-1">申请结算金额</span>
                  <span className="font-bold text-foreground-950 text-base">
                    {formatMoney(detail.apply_amount, detail.currency, true)}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-foreground-400 block mb-1">关联账单 ID</span>
                  <span className="font-mono text-foreground-700 text-xs">{detail.statement_id || '-'}</span>
                </div>
                <div>
                  <span className="text-xs text-foreground-400 block mb-1">关联结算资料 ID</span>
                  <span className="font-mono text-foreground-700 text-xs">{detail.settlement_profile_id || '-'}</span>
                </div>
              </div>

              {/* 2. 状态与时间节点轨迹 */}
              <div>
                <h4 className="font-semibold text-foreground-900 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <i className="ri-history-line text-primary-500"></i>
                  时间节点轨迹
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 text-xs">
                  <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200/50">
                    <span className="text-foreground-400 block text-[11px] mb-0.5">申请提交时间</span>
                    <span className="font-medium text-foreground-800">{formatDate(detail.applied_at || detail.created_at)}</span>
                  </div>
                  <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200/50">
                    <span className="text-foreground-400 block text-[11px] mb-0.5">所有者确认时间</span>
                    <span className="font-medium text-foreground-800">{formatDate(detail.owner_confirmed_at)}</span>
                  </div>
                  <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200/50">
                    <span className="text-foreground-400 block text-[11px] mb-0.5">平台审核完成时间</span>
                    <span className="font-medium text-foreground-800">{formatDate(detail.reviewed_at)}</span>
                  </div>
                  <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200/50">
                    <span className="text-foreground-400 block text-[11px] mb-0.5">打款到账时间</span>
                    <span className="font-medium text-foreground-800">{formatDate(payment?.paid_at || detail.issued_at)}</span>
                  </div>
                  <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200/50">
                    <span className="text-foreground-400 block text-[11px] mb-0.5">异议提交时间</span>
                    <span className="font-medium text-foreground-800">{formatDate(detail.dispute_submitted_at)}</span>
                  </div>
                </div>
              </div>

              {/* 3. 包含的结算明细列表 (items) */}
              {detail.items && detail.items.length > 0 && (
                <div>
                  <h4 className="font-semibold text-foreground-900 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <i className="ri-list-check-2 text-primary-500"></i>
                    结算明细项列表 ({detail.items.length})
                  </h4>
                  <div className="overflow-x-auto rounded-xl border border-gray-200/70">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-gray-50 border-b border-gray-200/70 text-foreground-500 font-medium">
                        <tr>
                          <th className="py-2.5 px-3">项目 / 渠道</th>
                          <th className="py-2.5 px-3">周期起止</th>
                          <th className="py-2.5 px-3 text-right">毛收入</th>
                          <th className="py-2.5 px-3 text-right">渠道/人工扣除</th>
                          <th className="py-2.5 px-3 text-right">可分配金额</th>
                          <th className="py-2.5 px-3 text-center">分成比例</th>
                          <th className="py-2.5 px-3 text-right">所有者分成</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {detail.items.map((item, idx) => (
                          <tr key={item.id || idx} className="hover:bg-gray-50/50">
                            <td className="py-2.5 px-3 font-medium text-foreground-900">
                              {item.project_id ? (
                                <Link
                                  to={`/projects/${item.project_id}`}
                                  onClick={onClose}
                                  className="text-primary-600 hover:text-primary-700 hover:underline font-semibold block"
                                >
                                  {item.project_title || item.project_no || '相关项目'}
                                </Link>
                              ) : (
                                <div>{item.project_title || item.project_no || '相关项目'}</div>
                              )}
                              {item.channel_name && (
                                <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                  {item.channel_name}
                                </span>
                              )}
                            </td>
                            <td className="py-2.5 px-3 text-foreground-600 whitespace-nowrap">
                              {item.period_start_date ? `${formatDate(item.period_start_date)} ~ ${formatDate(item.period_end_date)}` : '-'}
                            </td>
                            <td className="py-2.5 px-3 text-right text-foreground-800">
                              {formatMoney(item.gross_income_amount, item.currency)}
                            </td>
                            <td className="py-2.5 px-3 text-right text-foreground-600">
                              {formatMoney((item.channel_deduction_amount || 0) + (item.manual_deduction_amount || 0), item.currency)}
                            </td>
                            <td className="py-2.5 px-3 text-right text-foreground-800">
                              {formatMoney(item.distributable_amount, item.currency)}
                            </td>
                            <td className="py-2.5 px-3 text-center font-medium text-foreground-700">
                              {formatRateText(item.owner_share_rate_snapshot)}
                            </td>
                            <td className="py-2.5 px-3 text-right font-semibold text-primary-600">
                              {formatMoney(item.owner_share_amount, item.currency)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* 4. 付款凭证与打款记录 (payment) */}
              {payment && (
                <div>
                  <h4 className="font-semibold text-foreground-900 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <i className="ri-secure-payment-line text-emerald-500"></i>
                    打款与支付凭证
                  </h4>
                  <div className="p-4 rounded-xl bg-emerald-50/40 border border-emerald-200/50 space-y-3 text-xs">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div>
                        <span className="text-foreground-400 block mb-0.5">付款方式</span>
                        <span className="font-semibold text-foreground-800">
                          {payment.payment_method === 'BANK_TRANSFER' ? '银行转账' : payment.payment_method || '银行转账'}
                        </span>
                      </div>
                      <div>
                        <span className="text-foreground-400 block mb-0.5">实际打款金额</span>
                        <span className="font-bold text-emerald-700 text-sm">
                          {formatMoney(payment.payment_amount ?? detail.apply_amount, payment.currency || detail.currency)}
                        </span>
                      </div>
                      <div>
                        <span className="text-foreground-400 block mb-0.5">打款时间</span>
                        <span className="font-medium text-foreground-800">{formatDate(payment.paid_at)}</span>
                      </div>
                      <div>
                        <span className="text-foreground-400 block mb-0.5 font-normal">状态</span>
                        {(() => {
                          const paymentStatusInfo = getRevenueStatusInfo(payment.status || 'PAYMENT_PAID');
                          return (
                            <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium whitespace-nowrap inline-block ${paymentStatusInfo.color}`}>
                              {paymentStatusInfo.label}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="pt-2 border-t border-emerald-200/40 flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-4 text-foreground-600">
                        {payment.payment_no && <span>打款单号: <code className="font-mono">{payment.payment_no}</code></span>}
                        {payment.bank_transaction_no && <span>银行流水: <code className="font-mono">{payment.bank_transaction_no}</code></span>}
                        {payment.remark && <span>备注: {payment.remark}</span>}
                      </div>
                      {payment.payment_proof_asset?.access_url && (
                        <a
                          href={payment.payment_proof_asset.access_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-800 font-medium underline"
                        >
                          <i className="ri-attachment-line"></i>
                          查看打款凭证 ({payment.payment_proof_asset.file_name || '支付水单'})
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* 5. 结算资料快照 (settlement_profile_snapshot) */}
              {Object.keys(snapshot).length > 0 && (
                <div>
                  <h4 className="font-semibold text-foreground-900 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <i className="ri-shield-user-line text-purple-500"></i>
                    结算资料快照 (申请时锁定)
                  </h4>
                  <div className="p-4 rounded-xl bg-purple-50/30 border border-purple-200/50 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                    {(snapshot.settlement_subject_name || snapshot.recipient_name) && (
                      <div>
                        <span className="text-foreground-400 block mb-0.5">结算主体 / 收款人</span>
                        <span className="font-medium text-foreground-900">
                          {snapshot.settlement_subject_name || snapshot.recipient_name}
                        </span>
                      </div>
                    )}
                    {snapshot.bank_name && (
                      <div>
                        <span className="text-foreground-400 block mb-0.5">开户银行</span>
                        <span className="font-medium text-foreground-900">
                          {snapshot.bank_name} {snapshot.bank_branch_name ? `(${snapshot.bank_branch_name})` : ''}
                        </span>
                      </div>
                    )}
                    {snapshot.bank_account_no && (
                      <div>
                        <span className="text-foreground-400 block mb-0.5">银行账号</span>
                        <span className="font-mono text-foreground-900">{snapshot.bank_account_no}</span>
                      </div>
                    )}
                    {snapshot.bank_account_name && (
                      <div>
                        <span className="text-foreground-400 block mb-0.5">开户户名</span>
                        <span className="font-medium text-foreground-900">{snapshot.bank_account_name}</span>
                      </div>
                    )}
                    {(snapshot.tax_no || snapshot.tax_material) && (
                      <div>
                        <span className="text-foreground-400 block mb-0.5">税号 / 报税材料</span>
                        <span className="font-mono text-foreground-900">{snapshot.tax_no || snapshot.tax_material}</span>
                      </div>
                    )}
                    {snapshot.invoice_title && (
                      <div>
                        <span className="text-foreground-400 block mb-0.5">发票抬头</span>
                        <span className="font-medium text-foreground-900">{snapshot.invoice_title}</span>
                      </div>
                    )}
                    {snapshot.finance_contact_name && (
                      <div>
                        <span className="text-foreground-400 block mb-0.5">财务联系人</span>
                        <span className="font-medium text-foreground-900">
                          {snapshot.finance_contact_name} {snapshot.finance_contact_phone ? `(${snapshot.finance_contact_phone})` : ''}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 6. 说明与审核备注 */}
              {(detail.applicant_note || detail.dispute_note || detail.audit_remark) && (
                <div className="space-y-2 text-xs">
                  {detail.applicant_note && (
                    <div className="p-3 rounded-xl bg-gray-50 border border-gray-200/60">
                      <span className="font-semibold text-foreground-800 block mb-0.5">申请人说明:</span>
                      <p className="text-foreground-600">{detail.applicant_note}</p>
                    </div>
                  )}
                  {detail.dispute_note && (
                    <div className="p-3 rounded-xl bg-rose-50/80 border border-rose-200/70 text-rose-900">
                      <span className="font-semibold block mb-0.5">内容方异议说明:</span>
                      <p>{detail.dispute_note}</p>
                    </div>
                  )}
                  {detail.audit_remark && (
                    <div className="p-3 rounded-xl bg-amber-50/80 border border-amber-200/70 text-amber-950">
                      <span className="font-semibold block mb-0.5">平台审核备注:</span>
                      <p>{detail.audit_remark}</p>
                    </div>
                  )}
                </div>
              )}

              {/* 7. 审核记录列表 (reviewRecords) */}
              {reviewRecords.length > 0 && (
                <div>
                  <h4 className="font-semibold text-foreground-900 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <i className="ri-audit-line text-blue-500"></i>
                    审核记录明细 ({reviewRecords.length})
                  </h4>
                  <div className="space-y-2 text-xs">
                    {reviewRecords.map((rec) => {
                      const reviewTypeInfo = getRevenueStatusInfo(rec.review_type);
                      const resultInfo = getRevenueStatusInfo(rec.result);
                      return (
                        <div key={rec.id} className="p-3 rounded-lg bg-gray-50 border border-gray-200/50 flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-foreground-900">
                                {rec.review_type ? reviewTypeInfo.label : '阶段审核'}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${resultInfo.color}`}>
                                {rec.result ? resultInfo.label : '已审核'}
                              </span>
                            </div>
                            {rec.review_remark && <p className="text-foreground-600 mt-1">{rec.review_remark}</p>}
                          </div>
                          <div className="text-foreground-400 text-right">
                            <div>{formatDate(rec.reviewed_at || rec.created_at)}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex flex-col gap-3">
          {showDisputeForm && (
            <div className="p-3 bg-white rounded-xl border border-rose-200 space-y-2.5 animate-fade-in">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-rose-800 flex items-center gap-1">
                  <i className="ri-feedback-line"></i> 提交账单异议说明
                </span>
                <button
                  type="button"
                  onClick={() => setShowDisputeForm(false)}
                  className="text-foreground-400 hover:text-foreground-700"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <textarea
                value={disputeNote}
                onChange={(e) => setDisputeNote(e.target.value)}
                placeholder="请详细说明异议原因（如分成比例不符、渠道数据有误等）..."
                rows={2}
                className="w-full text-xs p-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-rose-400 resize-none text-foreground-800"
              />
              <div className="flex justify-end gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => setShowDisputeForm(false)}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-foreground-700"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={handleDispute}
                  disabled={submittingDispute}
                  className="px-3 py-1.5 rounded-lg bg-rose-600 text-white font-medium hover:bg-rose-500 disabled:opacity-50"
                >
                  {submittingDispute ? '提交中...' : '确认提交异议'}
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="text-xs text-foreground-400">
              如有问题可联系平台财务协助核对
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-xl border border-gray-200 hover:bg-gray-100 text-foreground-700 text-xs font-medium transition-colors cursor-pointer"
              >
                关闭
              </button>
              {canDispute && !showDisputeForm && (
                <button
                  onClick={() => setShowDisputeForm(true)}
                  className="px-4 py-2 rounded-xl border border-rose-200 text-rose-700 bg-rose-50/50 hover:bg-rose-100 text-xs font-medium transition-all cursor-pointer"
                >
                  提交异议
                </button>
              )}
              {canConfirm && (
                <button
                  onClick={handleConfirm}
                  disabled={confirming}
                  className="px-4 py-2 rounded-xl bg-primary-500 text-foreground-950 text-xs font-medium hover:bg-primary-400 transition-all cursor-pointer disabled:opacity-50"
                >
                  {confirming ? '确认中...' : '确认结算'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
